import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { getTeamUtilization } from '../../api/reportsApi';
import { usePermissions } from '../PermissionsContext';
import './Reports.css';

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmtH = (v) => (Number(v) || 0).toLocaleString('en-US', { maximumFractionDigits: 1 });
const fmtPct = (v) => `${(Number(v) || 0).toFixed(1)}%`;
const barClass = (u) => (u > 110 ? 'red' : u < 60 ? 'amber' : 'green');

const buildMonthOptions = (count) => {
  const opts = [];
  const now = new Date();
  for (let i = 0; i < count; i++) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    opts.push({ value: `${d.getFullYear()}-${d.getMonth() + 1}`, label: `${MONTH_NAMES[d.getMonth() + 1]} ${d.getFullYear()}`, year: d.getFullYear(), month: d.getMonth() + 1 });
  }
  return opts;
};

export default function TeamUtilizationPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const allowed = hasPermission?.('Timesheet.ViewProjectReports');

  const monthOptions = useMemo(() => buildMonthOptions(12), []);
  const [monthVal, setMonthVal] = useState(monthOptions[0].value);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const selected = monthOptions.find((o) => o.value === monthVal) || monthOptions[0];

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dto = await getTeamUtilization(selected.year, selected.month, null);
      setData(dto);
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load team utilization.');
    } finally {
      setLoading(false);
    }
  }, [selected.year, selected.month]);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  if (!permLoading && !allowed) {
    return <div className="reports-page"><p className="empty-note">You don't have access to project reports.</p></div>;
  }

  const rows = data?.rows || [];
  const utilColor = (data?.teamUtilizationPercent ?? 0) >= 75 ? 'green' : (data?.teamUtilizationPercent ?? 0) >= 60 ? 'amber' : 'red';

  return (
    <div className="reports-page" data-testid="team-utilization">
      <div className="breadcrumb">Reports · Team Utilization</div>
      <h1 className="page-title">Team Utilization</h1>
      <p className="page-sub">Billable utilisation from actual timesheet entries (logged billable hours / available hours).</p>

      <div className="filter-bar">
        <label htmlFor="tu-month">Month</label>
        <select id="tu-month" value={monthVal} onChange={(e) => setMonthVal(e.target.value)} data-testid="tu-month">
          {monthOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
        <label htmlFor="tu-team">Team</label>
        <select id="tu-team" disabled data-testid="tu-team"><option>All</option></select>
      </div>

      {error && <div className="report-error">{error}</div>}

      <div className="kpi-row">
        <div className="kpi">
          <div className="kpi-label">Headcount</div>
          <div className="kpi-value">{data?.headcount ?? 0}</div>
        </div>
        <div className={`kpi ${utilColor}`}>
          <div className="kpi-label">Team Utilization</div>
          <div className="kpi-value">{fmtPct(data?.teamUtilizationPercent)}</div>
        </div>
        <div className="kpi amber">
          <div className="kpi-label">Under-utilized</div>
          <div className="kpi-value">{data?.underUtilizedCount ?? 0}</div>
          <div className="kpi-sub">&lt; 60%</div>
        </div>
        <div className="kpi red">
          <div className="kpi-label">Over-allocated</div>
          <div className="kpi-value">{data?.overAllocatedCount ?? 0}</div>
          <div className="kpi-sub">&gt; 110%</div>
        </div>
      </div>

      <div className="panel">
        {loading ? (
          <div className="empty-note">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty-note">No consultants for this period.</div>
        ) : (
          <table className="cells" data-testid="tu-table">
            <thead>
              <tr><th>Consultant</th><th className="num">Billable h</th><th className="num">Available h</th><th className="num">Utilization</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.employeeId} data-testid={`tu-row-${r.employeeId}`}>
                  <td>{r.name}</td>
                  <td className="num">{fmtH(r.billableHours)}</td>
                  <td className="num">{fmtH(r.availableHours)}</td>
                  <td className="num">
                    <div className="bar-wrap">
                      <div className="bar"><div className={`bar-fill ${barClass(r.utilizationPercent)}`} style={{ width: `${Math.min(r.utilizationPercent, 100)}%` }} /></div>
                      <span className="bar-text">{fmtPct(r.utilizationPercent)}</span>
                    </div>
                  </td>
                </tr>
              ))}
              {(() => {
                // Hours columns sum; utilization is the AVERAGE across visible rows.
                const sumBill = rows.reduce((s, r) => s + (Number(r.billableHours) || 0), 0);
                const sumAvail = rows.reduce((s, r) => s + (Number(r.availableHours) || 0), 0);
                const avgUtil = rows.length
                  ? rows.reduce((s, r) => s + (Number(r.utilizationPercent) || 0), 0) / rows.length
                  : 0;
                return (
                  <tr className="tu-totals-row" data-testid="tu-totals-row">
                    <td>Total</td>
                    <td className="num" data-testid="tu-total-billable">{fmtH(sumBill)}</td>
                    <td className="num" data-testid="tu-total-available">{fmtH(sumAvail)}</td>
                    <td className="num" data-testid="tu-total-util">
                      <span className={`tu-util-${barClass(avgUtil)}`}>{fmtPct(avgUtil)}</span>
                    </td>
                  </tr>
                );
              })()}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
